import { isNaN, isNumber, isDate, isNullOrUndefined } from './typeChecks';
import intlInstanceCache from './intlInstanceCache';
import { TFormatType } from '../models/data/TFormatType';
import { TTranslateFn } from '../models/common';
import { IFormatter, IFormatOptions } from '../models/data';
import { IDateSymbol } from '../models/data/IDateSymbol';
import { ITransform } from '../models/data/ITransform';
import { transform } from './transform';
import { sortBy } from './collection';
import { INumberThresholds } from '../models/data/INumberThresholds';
import { formatDateWithStringFormat } from './date';

export const padStart = (value: any, length: number, padder: string = '0') => {
	if (value !== undefined && value !== null) {
		let response: string = value.toString();
		if (response.length < length) {
			const split = response.split('');
			while (split.length < length) {
				split.unshift(padder);
			}
			response = split.join('');
		}
		return response;
	}
	return '';
};

export const formatNumber = (
	locale: string = 'en-GB',
	options: IFormatOptions,
	value: number,
	translate?: TTranslateFn,
) => {
	if (isNumber(value)) {
		try {
			const { numberThresholds, thresholdSeparator = '', forceMinimumThresholdFormatting = false } = options;
			let numberThreshold: INumberThresholds | undefined;
			if (numberThresholds) {
				const sortedNumberThresholds = sortBy(numberThresholds, item => item.value.toString(), 'DESC');
				for (const numberGroup of sortedNumberThresholds) {
					const { value: thresholdValue } = numberGroup;
					const shouldFormat = sortedNumberThresholds.indexOf(numberGroup) === sortedNumberThresholds.length - 1 && value < thresholdValue && forceMinimumThresholdFormatting;
					if (value >= thresholdValue || shouldFormat) {
						numberThreshold = numberGroup;
						value = value / thresholdValue;
						break;
					}
				}
			}
			const formattedNumber = intlInstanceCache.getNumber(locale, options).format(value);
			if (numberThreshold) {
				const { text, separator = thresholdSeparator, displayPosition = 'suffix' } = numberThreshold;
				const translatedText = (translate && translate(text)) || text;
				if (displayPosition === 'suffix') {
					return `${formattedNumber}${separator}${translatedText}`;
				}
				if (displayPosition === 'prefix') {
					return `${translatedText}${separator}${formattedNumber}`;
				}
			}
			return formattedNumber;
		}
		catch (e) {
			console.warn(e);
			return value;
		}
	}

	return value;
};

const getFormatSymbolData = (format: string, symbolChar: string): IDateSymbol => {
	const formatCharacters = format.split('');
	let start;
	let length = 0;

	for (let i = 0; i < formatCharacters.length; i++) {
		const char = formatCharacters[i];
		const prevChar = i > 0 ? formatCharacters[i - 1] : null;
		if (start === undefined) {
			if (char === symbolChar) {
				start = i;
				length++;
			}
		}
		else if (prevChar === symbolChar && char === symbolChar) {
			length++;
		}
	}

	return {
		start,
		length,
		key: format.slice(start, start + length),
	};
};

const getDatePart = (date: string, symbol: IDateSymbol, key: string, defaultPart?: string) => {
	if (date && symbol && symbol.length > 0 && symbol.key === key) {
		return date.slice(symbol.start, symbol.start + symbol.length);
	}
	return defaultPart;
};

const dateFromFormat = (date: string, fromFormat: string): Date => {
	if (date && fromFormat !== 'YYYY-MM-DD') {
		const symbols = {
			year: getFormatSymbolData(fromFormat, 'Y'),
			month: getFormatSymbolData(fromFormat, 'M'),
			day: getFormatSymbolData(fromFormat, 'D'),
			hour: getFormatSymbolData(fromFormat, 'H'),
			minute: getFormatSymbolData(fromFormat, 'm'),
			second: getFormatSymbolData(fromFormat, 'S'),
		};

		let formattedDateString = '';
		formattedDateString += `${getDatePart(date, symbols.year, 'YYYY')}-`;
		formattedDateString += `${getDatePart(date, symbols.month, 'MM')}-`;
		formattedDateString += `${getDatePart(date, symbols.day, 'DD')}T`;
		formattedDateString += `${getDatePart(date, symbols.hour, 'HH', '00')}:`;
		formattedDateString += `${getDatePart(date, symbols.minute, 'mm', '00')}:`;
		formattedDateString += `${getDatePart(date, symbols.second, 'SS', '00')}`;

		return new Date(formattedDateString);
	}
	return new Date(date);
};

export const formatDateTime = (
	locale: string = 'en-GB',
	options: IFormatOptions,
	value: string,
	translate?: TTranslateFn,
) => {
	const workingOptions = {
		timeZone: 'UTC',
		...options,
	}
	const fromDateFormat = workingOptions.fromDateFormat || 'YYYY-MM-DD';

	// Cleans up dates coming in with a format of YYYY-MM-DD HH:mm:SS, it will change them to
	// YYYY-MM-DDTHH:mm:SS. This is required for how IE11 handles value format for the new Date(value).
	const dateRegex = new RegExp(/(\d){4}-(\d){2}-(\d){2} (\d){2}\:(\d){2}\:(\d){2}/);
	if (typeof value === 'string' && dateRegex.test(value)) {
		value = value.replace(' ', 'T');
	}

	const date = dateFromFormat(value, fromDateFormat);

	const isValidDate = isDate(date) && !isNaN(date.getTime());

	if (isValidDate) {
		try {
			const { dateFormatString } = workingOptions;
			if (dateFormatString) {
				return formatDateWithStringFormat(date, dateFormatString, translate);
			}
			return intlInstanceCache.getDateTime(locale, workingOptions).format(date);
		}
		catch (e) {
			console.warn(e);
			return value;
		}
	}

	return value;
};

export const formatter = (format: IFormatter, value: number | string, translate?: TTranslateFn) => {
	const { type, locale, currency, options, transforms } = format;
	return spreadFormatter(type, locale, currency, options, value, transforms, translate);
};

const spreadFormatter = (
	type: TFormatType,
	locale: string = 'en-GB',
	currency: string | undefined,
	options: IFormatOptions = {},
	value: number | string,
	transforms?: ITransform[],
	translate?: TTranslateFn,
) => {
	let response = value;
	const { placeholderText, placeholderKey = 'value' } = options;
	if (transforms && isNumber(value)) {
		const typesAllowedToTransform: TFormatType[] = ['number', 'percent', 'currency'];
		if (typesAllowedToTransform.includes(type)) {
			value = transform(value, transforms);
		}
	}

	if (type === 'string') {
		response = !isNullOrUndefined(value) && value.toString();
	}

	if (type === 'number') {
		response = formatNumber(locale, options, value as number, translate);
	}

	if (type === 'percent') {
		const workingOptions = Object.assign({}, options || {}, { style: 'percent' });
		response = formatNumber(locale, workingOptions, value as number, translate);
	}

	if (type === 'currency') {
		const workingOptions = Object.assign({}, options || {}, { style: 'currency' });
		workingOptions.currency = currency;
		response = formatNumber(locale, workingOptions, value as number, translate);
	}

	if (type === 'date') {
		response = formatDateTime(locale, options, value as string);
	}

	if (translate && placeholderText) {
		response = translate(placeholderText, { [placeholderKey]: response });
	}

	return response;
};

export default spreadFormatter;
